package com.devi3east.bashbuddy

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String
import kotlin.collections.List

public class RecipeEntityQueries(
  driver: SqlDriver,
  private val RecipeAdapter: Recipe.Adapter,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectAllRecipes(mapper: (
    id: Long,
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
  ) -> T): Query<T> = Query(2_025_780_870, arrayOf("Recipe"), driver, "RecipeEntity.sq",
      "selectAllRecipes",
      "SELECT Recipe.id, Recipe.title, Recipe.description, Recipe.category, Recipe.area, Recipe.imageUrl, Recipe.youtubeLink, Recipe.ingredients, Recipe.instructions, Recipe.isFavorite, Recipe.rating, Recipe.duration, Recipe.difficulty FROM Recipe") {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      RecipeAdapter.ingredientsAdapter.decode(cursor.getString(7)!!),
      RecipeAdapter.instructionsAdapter.decode(cursor.getString(8)!!),
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)
    )
  }

  public fun selectAllRecipes(): Query<Recipe> = selectAllRecipes { id, title, description,
      category, area, imageUrl, youtubeLink, ingredients, instructions, isFavorite, rating,
      duration, difficulty ->
    Recipe(
      id,
      title,
      description,
      category,
      area,
      imageUrl,
      youtubeLink,
      ingredients,
      instructions,
      isFavorite,
      rating,
      duration,
      difficulty
    )
  }

  public fun <T : Any> selectRecipeById(id: Long, mapper: (
    id: Long,
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
  ) -> T): Query<T> = SelectRecipeByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      RecipeAdapter.ingredientsAdapter.decode(cursor.getString(7)!!),
      RecipeAdapter.instructionsAdapter.decode(cursor.getString(8)!!),
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)
    )
  }

  public fun selectRecipeById(id: Long): Query<Recipe> = selectRecipeById(id) { id_, title,
      description, category, area, imageUrl, youtubeLink, ingredients, instructions, isFavorite,
      rating, duration, difficulty ->
    Recipe(
      id_,
      title,
      description,
      category,
      area,
      imageUrl,
      youtubeLink,
      ingredients,
      instructions,
      isFavorite,
      rating,
      duration,
      difficulty
    )
  }

  public fun <T : Any> searchRecipeByText(query: String, mapper: (
    id: Long,
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
  ) -> T): Query<T> = SearchRecipeByTextQuery(query) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      RecipeAdapter.ingredientsAdapter.decode(cursor.getString(7)!!),
      RecipeAdapter.instructionsAdapter.decode(cursor.getString(8)!!),
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)
    )
  }

  public fun searchRecipeByText(query: String): Query<Recipe> = searchRecipeByText(query) { id,
      title, description, category, area, imageUrl, youtubeLink, ingredients, instructions,
      isFavorite, rating, duration, difficulty ->
    Recipe(
      id,
      title,
      description,
      category,
      area,
      imageUrl,
      youtubeLink,
      ingredients,
      instructions,
      isFavorite,
      rating,
      duration,
      difficulty
    )
  }

  public suspend fun insertRecipe(
    id: Long?,
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
  ) {
    driver.execute(1_551_365_453, """
        |INSERT INTO Recipe(id, title, description, category, area, imageUrl, youtubeLink, ingredients, instructions, isFavorite, rating, duration, difficulty)
        |VALUES(?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
        |)
        """.trimMargin(), 13) {
          bindLong(0, id)
          bindString(1, title)
          bindString(2, description)
          bindString(3, category)
          bindString(4, area)
          bindString(5, imageUrl)
          bindString(6, youtubeLink)
          bindString(7, RecipeAdapter.ingredientsAdapter.encode(ingredients))
          bindString(8, RecipeAdapter.instructionsAdapter.encode(instructions))
          bindLong(9, isFavorite)
          bindLong(10, rating)
          bindString(11, duration)
          bindString(12, difficulty)
        }.await()
    notifyQueries(1_551_365_453) { emit ->
      emit("Recipe")
    }
  }

  public suspend fun updateRecipe(
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
    id: Long,
  ) {
    driver.execute(-142_958_755, """
        |UPDATE Recipe
        | SET title = ?,
        |    description = ?,
        |    category = ?,
        |    area = ?,
        |    imageUrl = ?,
        |    youtubeLink = ?,
        |    ingredients = ?,
        |    instructions = ?,
        |    isFavorite = ?,
        |    rating = ?,
        |    duration = ?,
        |    difficulty = ?
        |    WHERE  id = ?
        """.trimMargin(), 13) {
          bindString(0, title)
          bindString(1, description)
          bindString(2, category)
          bindString(3, area)
          bindString(4, imageUrl)
          bindString(5, youtubeLink)
          bindString(6, RecipeAdapter.ingredientsAdapter.encode(ingredients))
          bindString(7, RecipeAdapter.instructionsAdapter.encode(instructions))
          bindLong(8, isFavorite)
          bindLong(9, rating)
          bindString(10, duration)
          bindString(11, difficulty)
          bindLong(12, id)
        }.await()
    notifyQueries(-142_958_755) { emit ->
      emit("Recipe")
    }
  }

  public suspend fun detelteRecipeById(id: Long) {
    driver.execute(-119_150_363, """DELETE FROM Recipe WHERE id = ?""", 1) {
          bindLong(0, id)
        }.await()
    notifyQueries(-119_150_363) { emit ->
      emit("FavoriteRecipe")
      emit("Recipe")
    }
  }

  public suspend fun deleteAllRecipes() {
    driver.execute(177_764_469, """DELETE FROM Recipe""", 0).await()
    notifyQueries(177_764_469) { emit ->
      emit("FavoriteRecipe")
      emit("Recipe")
    }
  }

  public suspend fun upsertRecipe(
    title: String,
    description: String,
    category: String,
    area: String,
    imageUrl: String,
    youtubeLink: String,
    ingredients: List<String>,
    instructions: List<String>,
    isFavorite: Long,
    rating: Long,
    duration: String?,
    difficulty: String?,
    id: Long,
  ) {
    transaction {
      driver.execute(-1_849_990_604, """
          |UPDATE Recipe
          |    SET title = ?,
          |    description = ?,
          |    category = ?,
          |    area = ?,
          |    imageUrl = ?,
          |    youtubeLink = ?,
          |    ingredients = ?,
          |    instructions = ?,
          |    isFavorite = ?,
          |    rating = ?,
          |    duration = ?,
          |    difficulty = ?
          |    WHERE id = ?
          """.trimMargin(), 13) {
            bindString(0, title)
            bindString(1, description)
            bindString(2, category)
            bindString(3, area)
            bindString(4, imageUrl)
            bindString(5, youtubeLink)
            bindString(6, RecipeAdapter.ingredientsAdapter.encode(ingredients))
            bindString(7, RecipeAdapter.instructionsAdapter.encode(instructions))
            bindLong(8, isFavorite)
            bindLong(9, rating)
            bindString(10, duration)
            bindString(11, difficulty)
            bindLong(12, id)
          }.await()
      driver.execute(-1_849_990_603, """
          |INSERT OR IGNORE INTO Recipe(id, title, description, category, area, imageUrl, youtubeLink, ingredients, instructions, isFavorite, rating, duration, difficulty)
          |    VALUES(?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
          """.trimMargin(), 13) {
            bindLong(0, id)
            bindString(1, title)
            bindString(2, description)
            bindString(3, category)
            bindString(4, area)
            bindString(5, imageUrl)
            bindString(6, youtubeLink)
            bindString(7, RecipeAdapter.ingredientsAdapter.encode(ingredients))
            bindString(8, RecipeAdapter.instructionsAdapter.encode(instructions))
            bindLong(9, isFavorite)
            bindLong(10, rating)
            bindString(11, duration)
            bindString(12, difficulty)
          }.await()
    }
    notifyQueries(-493_544_637) { emit ->
      emit("Recipe")
    }
  }

  private inner class SelectRecipeByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("Recipe", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("Recipe", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-799_731_390,
        """SELECT Recipe.id, Recipe.title, Recipe.description, Recipe.category, Recipe.area, Recipe.imageUrl, Recipe.youtubeLink, Recipe.ingredients, Recipe.instructions, Recipe.isFavorite, Recipe.rating, Recipe.duration, Recipe.difficulty FROM Recipe WHERE id = ?""",
        mapper, 1) {
      bindLong(0, id)
    }

    override fun toString(): String = "RecipeEntity.sq:selectRecipeById"
  }

  private inner class SearchRecipeByTextQuery<out T : Any>(
    public val query: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("Recipe", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("Recipe", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_072_272_832,
        """SELECT Recipe.id, Recipe.title, Recipe.description, Recipe.category, Recipe.area, Recipe.imageUrl, Recipe.youtubeLink, Recipe.ingredients, Recipe.instructions, Recipe.isFavorite, Recipe.rating, Recipe.duration, Recipe.difficulty FROM Recipe WHERE title LIKE ('%' || ? || '%') OR description LIKE ('%' || ? || '%') OR ingredients LIKE ('%' || ? || '%')""",
        mapper, 3) {
      bindString(0, query)
      bindString(1, query)
      bindString(2, query)
    }

    override fun toString(): String = "RecipeEntity.sq:searchRecipeByText"
  }
}
